'use client';
import React, { useState, useRef, useEffect } from 'react';
import { COUNTRY_PHONES, CountryPhone, formatPhoneDisplay, getCountryByCode } from '@/lib/phoneUtils';

interface PhoneInputProps {
  value: string;           // full phone e.g. "+254712345678"
  onChange: (full: string, digits: string, country: CountryPhone) => void;
  defaultCountry?: string; // ISO code e.g. "KE"
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
}

export default function PhoneInput({
  value,
  onChange,
  defaultCountry = 'KE',
  disabled = false,
  className = '',
  label,
  required = false,
  error,
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<CountryPhone>(() => {
    // Try to detect country from value
    if (value) {
      const match = COUNTRY_PHONES.find(c => value.startsWith(c.dialCode));
      if (match) return match;
    }
    return getCountryByCode(defaultCountry);
  });
  const [digits, setDigits] = useState<string>(() => {
    if (value && value.startsWith(selectedCountry.dialCode)) {
      return value.slice(selectedCountry.dialCode.length).replace(/\D/g, '');
    }
    if (value && !value.startsWith('+')) return value.replace(/\D/g, '');
    return '';
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDigitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, selectedCountry.maxDigits);
    setDigits(raw);
    onChange(`${selectedCountry.dialCode}${raw}`, raw, selectedCountry);
  };

  const handleCountrySelect = (country: CountryPhone) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
    setSearch('');
    onChange(`${country.dialCode}${digits}`, digits, country);
  };

  const filtered = search
    ? COUNTRY_PHONES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dialCode.includes(search) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : COUNTRY_PHONES;

  const displayValue = formatPhoneDisplay(digits, selectedCountry);

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-xs font-500 text-foreground">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      <div className="flex gap-2">
        {/* Country selector */}
        <div className="relative flex-shrink-0" ref={dropdownRef}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-input bg-muted text-sm text-foreground hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[90px]"
          >
            <span className="text-base leading-none">{selectedCountry.flag}</span>
            <span className="font-500 text-xs">{selectedCountry.dialCode}</span>
            <svg className="w-3 h-3 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {dropdownOpen && (
            <div className="absolute left-0 top-full mt-1 w-64 bg-card border border-border rounded-xl shadow-elevated z-50 overflow-hidden">
              <div className="p-2 border-b border-border">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search country…"
                  autoFocus
                  className="w-full px-2.5 py-1.5 rounded-lg border border-input bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filtered.map(country => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted transition-colors text-left ${selectedCountry.code === country.code ? 'bg-primary/5 text-primary' : 'text-foreground'}`}
                  >
                    <span className="text-base">{country.flag}</span>
                    <span className="flex-1 truncate text-xs">{country.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{country.dialCode}</span>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p className="px-3 py-4 text-xs text-muted-foreground text-center">No countries found</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone number input */}
        <input
          type="tel"
          value={displayValue}
          onChange={handleDigitsChange}
          disabled={disabled}
          placeholder={selectedCountry.placeholder}
          className="flex-1 px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground"
        />
      </div>
      {error && <p className="text-[10px] text-danger">{error}</p>}
    </div>
  );
}
